import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";

// dls
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// internal-deps
import { CartItemWithUUID, ItemImagesOnCartXPVariants } from "@swiggy-private/cart-types";
import { isEmpty, isNonEmptyString } from "@swiggy-private/checkout-core";

// components
import { ItemPrice } from "../components/ItemPrice";
import { AddButton } from "../components/AddButton";
import { ItemQty } from "../components/ItemQty";
import DeleteIcon from "../icons/Delete";
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";
import ItemQtyNonEditable from "../components/ItemQtyNonEditable";

// styles
import styles from "../styles/FoodItem.scss";

// constants
import {
    CART_ITEM_IMAGE_PLACEHOLDER,
    CART_ITEM_IMAGE_SIZE,
    OFFER_LIMIT_REACHED_TEXT,
    QTY_CHANGE_ACTION_TYPE,
} from "../constants";

// test ids
import { CART_ITEMS_TEST_IDS, QTY_INPUT_TEST_IDS } from "@swiggy-private/cart-test-ids";

// helper
import { getItemPriceClassName } from "../helper";
import { isItemCustomizable } from "@cart-ui/helpers/Item";

// hooks
import {
    useItemGroupPriceColor,
    useItemGroupOfferLimitColor,
    useMagicItemsOfferLimitImpression,
} from "@cart-ui/components/food-item/hooks/useItemGroup";

// style components
import { AddButtonContainer } from "../styles/ItemQty";

interface FoodItemTypeInStockProps {
    children: ReactElement;
    item: CartItemWithUUID;
    disableActions: boolean;
    onQuantityChange: (action: QTY_CHANGE_ACTION_TYPE, qty?: number) => void;
    updating: boolean;
    quantity: number;
    freeQuantity: number;
    removeItem: VoidFunction;
    showInputQtyField?: boolean;
    variant?: ItemImagesOnCartXPVariants;
    onItemImageLoad: () => void;
    isHost?: boolean;
    isGroupOrderFlow?: boolean;
    isCurrentItemFromSameUser?: boolean;
    onImpressionMagicItemsOfferLimit?: (itemId: string, qty: number) => void;
}

export const FoodItemTypeInStock = React.memo(function FoodItemTypeInStock(
    props: FoodItemTypeInStockProps,
): ReactElement {
    const itemPrice = props.item.subtotal;
    const finalPrice = props.item.final_price;
    const itemQuantity = props.item.quantity;

    const customPriceColor = useItemGroupPriceColor({
        itemGroupTagId: props.item.item_group_tag_id,
        color: props.item.color,
    });

    const offerLimitTextColor = useItemGroupOfferLimitColor({
        itemGroupTagId: props.item.item_group_tag_id,
        color: props.item.color,
    });

    const [isQtyBeingEdited, setIsQtyBeingEdited] = useState(false);
    const [isLimitTextShaking, setIsLimitTextShaking] = useState(false);
    const shakeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (shakeTimeoutRef.current) {
                clearTimeout(shakeTimeoutRef.current);
            }
        };
    }, []);

    const itemPriceClassName = useMemo(() => {
        return getItemPriceClassName(props.showInputQtyField);
    }, [props.showInputQtyField]);

    const renderQtyOrPrice = useCallback(
        (isQtyBeingEdited: boolean, priceClasname: string | undefined, hideMargin: boolean) => {
            return isQtyBeingEdited ? (
                <button
                    onMouseDown={props.removeItem}
                    className={styles.deleteIcon}
                    data-testid={QTY_INPUT_TEST_IDS.DELETE}
                >
                    <DeleteIcon />
                </button>
            ) : (
                <ItemPrice
                    itemPrice={itemPrice}
                    finalPrice={finalPrice}
                    freeQuantity={props.freeQuantity}
                    isInputQtyField={props.showInputQtyField}
                    className={priceClasname}
                    withoutMargin={hideMargin}
                    itemQuantity={itemQuantity}
                    customPriceColor={customPriceColor}
                />
            );
        },
        [
            props.removeItem,
            props.freeQuantity,
            props.showInputQtyField,
            itemPrice,
            finalPrice,
            itemQuantity,
            customPriceColor,
        ],
    );

    const handleMaxQuantityReached = useCallback(() => {
        if (props.item.disable_increase_qty) {
            setIsLimitTextShaking(true);
            shakeTimeoutRef.current = setTimeout(() => {
                setIsLimitTextShaking(false);
            }, 300);
        }
    }, [props.item.disable_increase_qty]);

    // Fire impression event when magic item offer limit is shown
    useMagicItemsOfferLimitImpression({
        disableIncreaseQty: props.item.disable_increase_qty,
        itemGroupTagId: props.item.item_group_tag_id,
        menuItemId: props.item.menu_item_id,
        quantity: props.item.quantity,
        onImpressionMagicItemsOfferLimit: props.onImpressionMagicItemsOfferLimit,
    });

    const renderItemsActions = useCallback(
        (className?: string, condensed?: boolean) => {
            if (props.disableActions) {
                return null;
            } else if (props.isGroupOrderFlow && !props.isHost && !props.isCurrentItemFromSameUser) {
                return (
                    <ItemQtyNonEditable quantity={String(props.quantity)} itemId={String(props.item?.menu_item_id)} />
                );
            } else if (props.showInputQtyField) {
                return (
                    <ItemQty
                        key={props.item?.menu_item_id}
                        quantity={props.quantity}
                        onQuantityChange={props.onQuantityChange}
                        updating={props.updating}
                        isQtyBeingEdited={isQtyBeingEdited}
                        setIsQtyBeingEdited={setIsQtyBeingEdited}
                        removeItem={props.removeItem}
                    />
                );
            } else {
                const addButtonClassName = cx(className, {
                    [styles.addButtonContainerLimitReached]: props.item.disable_increase_qty,
                });

                return (
                    <AddButtonContainer>
                        <AddButton
                            quantity={props.quantity}
                            onQuantityChange={props.onQuantityChange}
                            updating={props.updating}
                            className={addButtonClassName}
                            condensed={condensed}
                            disableIncrement={props.item.disable_increase_qty}
                            customBorderColor={props.item.disable_increase_qty ? offerLimitTextColor : undefined}
                            onMaxQuantityReached={handleMaxQuantityReached}
                        />

                        {props.item.disable_increase_qty && (
                            <Text
                                variant="V2.body3.neutral.regular"
                                color={offerLimitTextColor}
                                className={cx(styles.addButtonContainerLimitReachedText, {
                                    [styles.addButtonContainerShake]: isLimitTextShaking,
                                })}
                            >
                                {props.item.item_limit_reached_message || OFFER_LIMIT_REACHED_TEXT}
                            </Text>
                        )}
                    </AddButtonContainer>
                );
            }
        },
        [
            props.disableActions,
            props.showInputQtyField,
            props.item?.menu_item_id,
            props.quantity,
            props.onQuantityChange,
            props.updating,
            props.removeItem,
            isQtyBeingEdited,
            props.isGroupOrderFlow,
            props.isHost,
            props.isCurrentItemFromSameUser,
            props.item.disable_increase_qty,
            props.item.item_limit_reached_message,
            offerLimitTextColor,
            handleMaxQuantityReached,
            isLimitTextShaking,
        ],
    );

    const renderItemWithBigImage = useCallback(() => {
        const actionsClassname = cx({
            [styles.foodItemActionsBigWithoutImage]: isEmpty(props.item.cloudinaryImageId),
            [styles.foodItemActionsBig]: !isEmpty(props.item.cloudinaryImageId),
        });
        const imageWrapperClassname = cx(styles.imageWraper, styles.imageWraperBig, {
            [styles.imageWraperBigNoImage]: isEmpty(props.item.cloudinaryImageId),
            [styles.imageWraperOutline]: !isEmpty(props.item.cloudinaryImageId),
        });
        const priceClassname = cx(styles.foodItemPriceBig, itemPriceClassName, {
            [styles.foodItemPriceBigLessMargin]: !isItemCustomizable(props.item),
        });

        return (
            <div className={styles.itemImageBigContainer}>
                <div className={styles.itemDetails}>
                    <FoodSymbolIconSvg vegType={props.item.isVeg} containerClassName={styles.symbolForBigImage} />
                    <div className={styles.bigImageInfoWrapper}>{props.children}</div>
                    {renderQtyOrPrice(isQtyBeingEdited, priceClassname, true)}
                </div>
                <div className={imageWrapperClassname}>
                    {props.item.cloudinaryImageId && (
                        <Image
                            cloudinaryId={props.item.cloudinaryImageId}
                            cloudinaryWidth={CART_ITEM_IMAGE_SIZE.BIG.WIDTH * 2}
                            height={`${CART_ITEM_IMAGE_SIZE.BIG.HEIGHT}px`}
                            width={`${CART_ITEM_IMAGE_SIZE.BIG.WIDTH}px`}
                            className={cx(styles.itemImage, styles.itemImageBig)}
                            onLoad={props.onItemImageLoad}
                            data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_IMAGE}
                        />
                    )}
                    {renderItemsActions(actionsClassname, true)}
                </div>
            </div>
        );
    }, [
        isQtyBeingEdited,
        itemPriceClassName,
        props.children,
        props.item,
        props.onItemImageLoad,
        renderItemsActions,
        renderQtyOrPrice,
    ]);

    const renderItemWithSmallImage = useCallback(() => {
        const clodinaryId = isNonEmptyString(props.item.cloudinaryImageId)
            ? props.item.cloudinaryImageId
            : CART_ITEM_IMAGE_PLACEHOLDER;

        return (
            <>
                <div className={cx(styles.imageWraper, styles.imageWraperSmall)}>
                    <Image
                        cloudinaryId={clodinaryId}
                        cloudinaryWidth={CART_ITEM_IMAGE_SIZE.SMALL * 2}
                        height={`${CART_ITEM_IMAGE_SIZE.SMALL}px`}
                        width={`${CART_ITEM_IMAGE_SIZE.SMALL}px`}
                        className={styles.itemImage}
                        onLoad={props.onItemImageLoad}
                        data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_IMAGE}
                    />
                    <FoodSymbolIconSvg
                        vegType={props.item.isVeg}
                        className={cx(styles.symbol, styles.symbolForSmallImage)}
                    />
                </div>
                {props.children}
                {renderItemsActions(styles.foodItemActions, true)}
                {renderQtyOrPrice(isQtyBeingEdited, cx(styles.foodItemPrice, itemPriceClassName), true)}
            </>
        );
    }, [
        isQtyBeingEdited,
        itemPriceClassName,
        props.children,
        props.item.cloudinaryImageId,
        props.item.isVeg,
        props.onItemImageLoad,
        renderItemsActions,
        renderQtyOrPrice,
    ]);

    const renderItem = useCallback(() => {
        return (
            <>
                {props.children}
                {renderItemsActions()}
                {renderQtyOrPrice(isQtyBeingEdited, itemPriceClassName, false)}
            </>
        );
    }, [isQtyBeingEdited, itemPriceClassName, props.children, renderItemsActions, renderQtyOrPrice]);

    return (
        <>
            {props.variant === ItemImagesOnCartXPVariants.BIG && renderItemWithBigImage()}
            {props.variant === ItemImagesOnCartXPVariants.SMALL && renderItemWithSmallImage()}
            {props.variant === ItemImagesOnCartXPVariants.DEFAULT && renderItem()}
        </>
    );
});
